import { Flex, Modal, Select, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { LeadCardBranchItemData } from '../../data';
import type { FC } from 'react';

const { Text } = Typography;

interface RecordBranchModalProps {
  isOpen: boolean;
  branches: LeadCardBranchItemData[];
  initialBranchId: LeadCardBranchItemData['id'] | null;
  isBranchesLoading: boolean;
  onConfirm(branchId: number): void;
  onCancel(): void;
}

const toNumericId = (
  branchId: LeadCardBranchItemData['id'] | null
): number | null => {
  if (branchId === null) return null;

  const numericId = typeof branchId === 'number' ? branchId : Number(branchId);

  return Number.isFinite(numericId) ? numericId : null;
};

export const RecordBranchModal: FC<RecordBranchModalProps> = ({
  isOpen,
  branches,
  initialBranchId,
  isBranchesLoading,
  onConfirm,
  onCancel,
}) => {
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);

  const branchOptions = useMemo(
    () =>
      branches
        .map(({ id, name }) => {
          const numericId = toNumericId(id);

          if (numericId === null) return null;

          return { value: numericId, label: name };
        })
        .filter(
          (option): option is { value: number; label: string } =>
            option !== null
        ),
    [branches]
  );

  const confirmHandler = useCallback(() => {
    if (selectedBranchId === null) return;

    onConfirm(selectedBranchId);
  }, [onConfirm, selectedBranchId]);

  const hasBranches = branchOptions.length > 0;
  const isConfirmDisabled = selectedBranchId === null || !hasBranches;

  useEffect(() => {
    if (!isOpen) {
      setSelectedBranchId(null);
      return;
    }

    setSelectedBranchId(toNumericId(initialBranchId));
  }, [initialBranchId, isOpen]);

  return (
    <Modal
      title="Создание записи"
      okText="Продолжить"
      cancelText="Отменить"
      open={isOpen}
      onCancel={onCancel}
      onOk={confirmHandler}
      okButtonProps={{ disabled: isConfirmDisabled }}
      destroyOnHidden
    >
      <Flex gap={16} vertical>
        <Text>Выберите филиал, в котором Вы хотите создать запись:</Text>
        <Select
          loading={isBranchesLoading}
          value={selectedBranchId}
          onChange={setSelectedBranchId}
          options={branchOptions}
          placeholder="Выберите филиал"
          notFoundContent={
            isBranchesLoading ? null : 'У клиента нет доступных филиалов'
          }
        />
      </Flex>
    </Modal>
  );
};
